import type { StaticEntityType } from "@config";
import { useViewContext } from "@editor-providers";
import {
  ensureMaxWidthSetting,
  hasBackground,
  hasBorder,
} from "@library/shared";
import { MaxWidthSettingEnum } from "@library/types";
import classNames from "classnames";
import { Fragment, type ReactNode, useRef } from "react";
import { useAnimationHandler } from "../hooks";
import { ComboStyled, Container } from "../styles/Combo.styled";
import "../styles/index.scss";
import type {
  ComboAnimationType,
  ComboSettingsType,
  EditorDataProps,
  EditorSlotsProps,
} from "../types";
import { Background } from "./Background";

export const ComboView = ({
  entity,
  children,
  editorData,
  editorSlots,
}: {
  children: ReactNode;
  entity: StaticEntityType;
  editorData?: EditorDataProps;
  editorSlots?: EditorSlotsProps;
}) => {
  const { view } = useViewContext();
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { settings, id } = entity as StaticEntityType<ComboSettingsType>;
  const isEditor = view === "editor";
  const { animation } = useAnimationHandler({
    ref,
    containerRef,
    settings,
  });

  const DND_STYLE = editorData?.dnd?.style || {};
  const DND_ATTRIBUTES = editorData?.dnd?.attributes || {};
  const DND_POSITION = editorData?.dnd?.insertPosition;
  const DND_IS_DRAGGING = editorData?.dnd?.isDragging;
  const DND_IS_SORTING = editorData?.dnd?.isSorting;
  const DND_LISTENERS = editorData?.dnd?.listeners || {};

  return (
    <Container
      ref={(r) => {
        // @ts-ignore
        containerRef.current = r;
        editorData?.dnd?.setNodeRef?.(r);
      }}
      $animation={animation}
      {...DATA_ATTRIBUTES({ settings, animation })}
      {...EDITOR_ATTRIBUTES({ id, isEditor, active: editorData?.isActive })}
      {...DND_ATTRIBUTES}
      {...DND_LISTENERS}
      style={DND_STYLE}
      className={classNames(
        "tw-relative tw-mb-[var(--combos-spacing-y)] tw-w-full [align-items:inherit] [justify-content:inherit] last:tw-mb-0",
        {
          "--active": editorData?.isActive,
          "--active-thread": editorData?.isActiveThread,
          "--has-variable": !!editorSlots?.variableSlot,
          "--is-dragging": DND_IS_DRAGGING,
        },
      )}
    >
      <ComboStyled
        {...STYLED_ATTRIBUTES({
          settings,
          animation,
          isSorting: !!DND_IS_SORTING,
          insertPosition: DND_POSITION,
        })}
        ref={ref}
        className={classNames(
          "combo-wrapper-fhbZZmFAR1 comboWrapper comboTopWrapper tw-relative tw-flex tw-w-full tw-outline-none",
          {
            fullheight: settings.minHeight === "screenFit",
          },
        )}
      >
        {Object.entries({
          actionsSlot: editorSlots?.actionsSlot,
          entityThreadLabel: editorSlots?.entityThreadLabel,
          coeditingEntityOutline: editorSlots?.coeditingEntityOutline,
          shortcutsPlaceholder: editorSlots?.shortcutsPlaceholder,
        }).map(([key, slot]) => slot && <Fragment key={key}>{slot}</Fragment>)}
        <div className="inner_wrapper tw-relative tw-flex tw-w-full tw-px-combo-padding-x tw-py-combo-padding-y [text-align:inherit] max-md:tw-px-combo-mobile-padding-x max-md:tw-py-combo-mobile-padding-y">
          {settings.name === "FullScreen" && <Background settings={settings} />}
          {children}
        </div>
        {editorSlots?.variableSlot}
      </ComboStyled>
    </Container>
  );
};

//  ======= ATTRIBUTE CREATION =======
const STYLED_ATTRIBUTES = ({
  settings,
  animation,
  isSorting,
  insertPosition,
}: {
  settings: ComboSettingsType;
  animation?: ComboAnimationType;
  isSorting: boolean;
  insertPosition?: "before" | "after";
}) => ({
  $animation: animation,
  $verticalAlignment: settings.verticalAlignment,
  $horizontalAlignment: settings.horizontalAlignment,
  $backgroundColor: settings.backgroundColor,
  $overlayOpacity: settings.overlayOpacity,
  $borderColor: settings.borderColor,
  $borderWidth: settings.borderWidth,
  $round: settings.round,
  $minHeight: settings.minHeight,
  $maxWidth: ensureMaxWidthSetting(
    settings.maxWidth,
    settings.name === "VTimeline"
      ? MaxWidthSettingEnum.medium
      : MaxWidthSettingEnum.wide,
  ),
  $comboName: settings.name,
  $insertPosition: insertPosition,
  $isSorting: isSorting,
});

const DATA_ATTRIBUTES = ({
  settings,
  animation,
}: {
  settings: ComboSettingsType;
  animation?: ComboAnimationType;
}) => {
  const payload: Record<string, string | number | boolean | undefined> = {
    "data-como-array-sets-per-line": settings.setsPerLine,
    "data-combo-narrator-style": settings.style,
    "data-combo-carousel-style": settings.carouselStyle,
    "data-combo-name": settings.name,
    "data-anchor-target": settings.isAnchorActive ? settings.anchorName : "",
    "data-has-background": hasBackground(settings.backgroundColor),
    "data-has-border": hasBorder(settings),
    "data-combo-min-height": settings.minHeight,
  };
  if (animation) {
    const animationPayload = {
      "data-is-combo-animated": true,
      "data-combo-animation-type": animation?.type,
      "data-combo-animation-speed": animation?.speed,
      "data-combo-animation-direction": animation?.direction,
    };
    Object.assign(payload, animationPayload);
  }
  return payload;
};

const EDITOR_ATTRIBUTES = ({
  id,
  isEditor = false,
  active,
}: {
  id: string;
  isEditor: boolean;
  active?: boolean;
}) => {
  const payload: Record<string, string | number> = {
    "data-layer-id": id,
    "data-layer-type": "combo",
  };
  if (active && isEditor) payload["tabIndex"] = 0;
  return payload;
};
//  ======= ATTRIBUTE CREATION =======
